import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Tooltip, IconButton, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../Context/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import CustomizeColumns from "../Component/CustomizeColumns";
import OrderEntry from "../Component/OrderEntry.jsx";
import ManageFiltersModal from "../Component/Filters/ManageFiltersModal";
import BondDetailsModal from "../Component/BondDetails/BondDetailsModal";
import { useActualOrderStore } from "../Store/actualOrderStore.js";
import { useBondCharacteristicStore } from "../Store/bondCharacteristicStore.js";
import { deleteFilter, updateBondCharacteristicStatus } from "../http/api";
import { useInventoryData } from "../Component/InventoryComponents/useInventoryHooks.js";
import { useSavedFilters } from "../Component/InventoryComponents/useSavedFilters.js";
import { downloadCSV } from "../Component/InventoryComponents/utils.js";
import InventoryTable from "../Component/InventoryComponents/InventoryTable.jsx";
import InventoryHeader from "../Component/InventoryComponents/InventoryHeader.jsx";
import UpdateCharacteristicsStatusModal from "../Component/InventoryComponents/UpdateCharacteristicsStatusModal.jsx";
import dayjs from "dayjs";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager"; // Import the utility
import { getPreference, setPreference } from "../Utils/userPreferenceManger"; // Import user preference manager
import { convertMaturityToYears } from "../Utils/filterUtils"; // Import the utility
import { fetchSecurityInfo } from "../http/api"; // Import the new API
import { stagingOrderFields, liveOrderFields } from "../Constants/stagingBlotter";

const Inventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const customizeButtonRef = useRef(null);
  // Initialize source based on location state or check for input
  const [source, setSource] = useState(() => {
    // If explicitly provided in state, use that
    if (location.state?.source) return location.state.source;
    // If criteria or any filter params exist in state, use filteredSource
    if (location.state?.criteria || Object.keys(location.state || {}).filter(key => key !== 'source').length > 0) {
      return "filteredSource";
    }
    // Default value
    return "noSource";
  });
  const [input, setInput] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectCusip, setSelectCusip] = useState("");
  const [inventoryId, setInventoryId] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  const [oeOpen, setOeOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [isManageFiltersOpen, setIsManageFiltersOpen] = useState(false);
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const [bondDetailsLoading, setBondDetailsLoading] = useState(false); // Loader state

  // New state for update characteristics modal
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  // Initialize default sorting to ytw1 desc when source is yieldtable so first fetch is sorted
  const [sortConfigForFetchingData, setSortConfigForFetchingData] = useState(() => {
    if (source === "yieldtable") return { columnToSort: "ytw1", sortingDirection: "desc" };
    if (source === "viewInventoryBondChar" || source === "viewInventoryLadder")
      return { columnToSort: "ytm", sortingDirection: "desc" };
    return null;
  });
  // Update sort state to match the new structure, initialize when source is yieldtable
  const [sortConfig, setSortConfig] = useState(() => {
    if (source === "yieldtable") return { key: "ytw1", direction: "desc" };
    if (source === "viewInventoryBondChar" || source === "viewInventoryLadder")
      return { key: "ytm", direction: "desc" };
    return { key: "", direction: "" };
  });

  // If source later changes to yieldtable, apply default sorting, but don't override an existing sort
  useEffect(() => {
    if (!sortConfigForFetchingData || !sortConfigForFetchingData.columnToSort) {
      if (source === "yieldtable") {
        const defaultSort = { columnToSort: "ytw1", sortingDirection: "desc" };
        setSortConfigForFetchingData(defaultSort);
        setSortConfig({ key: "ytw1", direction: "desc" });
      } else if (source === "viewInventoryBondChar" || source === "viewInventoryLadder") {
        const defaultSort = { columnToSort: "ytm", sortingDirection: "desc" };
        setSortConfigForFetchingData(defaultSort);
        setSortConfig({ key: "ytm", direction: "desc" });
      }
    }
  }, [source]); 
    const handleSortingDataFetching = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction });
    setSortConfigForFetchingData({
      columnToSort: columnKey,
      sortingDirection: direction
    });
  };
  const { bondData, totalElements, lastUpdatedAt, loading } = useInventoryData(token, input, page, itemsPerPage, source,sortConfigForFetchingData);
  const { savedFilters, setSavedFilters } = useSavedFilters(token);

  const { setActualOrderData: setActualOrderDataZus, resetActualOrderData } = useActualOrderStore();
  const { characteristicId, bondLadderId, bondLadderRungId, resetBondCharacteristicId } = useBondCharacteristicStore();


  // Check conditions to show update status modal
  useEffect(() => {
    // Show the modal when source is "inventory", bondData is empty, and characteristicId exists
    if (bondData && bondData.length === 0 && characteristicId !== null && !loading) {
      setUpdateStatusModalOpen(true);
    }else{
      setUpdateStatusModalOpen(false);
    }
  }, [source, bondData, characteristicId, loading]);

  // Handler for updating characteristic status
  const handleUpdateCharacteristicStatus = async (comment) => {
    try {
      await updateBondCharacteristicStatus(token, characteristicId, comment);
      navigate(`/order?view=0&tab=1`);
    } catch (err) {
      // Pass error to modal instead of logging
      throw new Error(err.message || "Failed to update characteristic status");
    } finally {
      // resetBondCharacteristicId();
      setUpdateStatusModalOpen(false);
    }
  };

  const allColumns = [
    { key: "action", name: "Side" },
    { key: "brokerDealer", name: "Broker" },
    { key: "state", name: "State" },
    { key: "ratingMoody", name: "Moody's" },
    { key: "ratingSnp", name: "S&P" },
    { key: "cusip", name: "CUSIP" },
    { key: "bondType", name: "Type" },
    { key: "bondSubType", name: "Sub Type" },
    { key: "securityCharge", name: "Factored Securities", width: 120 },
    { key: "poolFactor", name: "Factor", width: 95 },
    { key: "description", name: "Description" },
    { key: "qty", name: "Qty" },
    { key: "minQty", name: "Min Qty" },
    { key: "maxQty", name: "Max Qty" },
    { key: "price", name: "Price" },
    { key: "coupon", name: "Coupon" },
    { key: "maturity", name: "Maturity" },
    { key: "callable", name: "Callable" },
    { key: "taxable", name: "Taxable" },
    { key: "issuer", name: "Issuer"},
    { key: "ytm", name: "YTM" },
    { key: "ytw1", name: "YTW" },
    { key: "ytc", name: "YTC" },
    { key: "accruedInterest", name: "Accrued Interest" },
    { key: "estimatedTotal", name: "Estimated Total" },
    { key: "duration", name: "Duration" },
    { key: "insured", name: "Insured" },
    { key: "paymentFrequency", name: "Payment Frequency" },
    { key: "sector", name: "Sector" },
    { key: "incomeSource", name: "Income Source" },
    { key: "nextCallDate", name: "Next Call Date" },
    { key: "altMinTax", name: "Alt Min Tax" },
    { key: "conventionMethod", name: "Convention Method" },
    { key: "underlyingIssuerRating", name: "Underlying Issuer Rating" },
  ];

  const [selectedColumns, setSelectedColumns] = useState(() => {
    // Check and update column configuration if needed
    // updateColumnConfigIfNeeded('inventory', allColumns);
    clearConfigIfOutdated('inventory', allColumns);
    // Then get the saved columns using role-based preferences
    const saved = getPreference('selectedColumns_inventory');
    return saved || [...allColumns];
  });
  const sampleData = { account: "GB3:GOV", bondType: "Treasury", quantity: "100000" };

  useEffect(() => {
    // Update source when input changes
    if (input && Object.keys(input).filter(key => key !== 'source').length > 0) {
      setSource("filteredSource");
    } else if (!input) {
      setSource("noSource");
    }
  }, [input]);

  // When location state changes, update input and handle source accordingly
  useEffect(() => {
    if (location.state && location.state.criteria) {
      setInput(location.state.criteria);
      setSource(location.state.source || "filteredSource"); // Ensure source is set
      if (location.state.filterName) {
        setSelectedFilter(location.state.filterName);
      }
    } else if (location.state) {
      setInput(location.state);
      // Set source to filteredSource if there are any valid filters in state
      const hasFilters = Object.keys(location.state).some(key => 
        key !== 'source' && location.state[key] !== undefined && location.state[key] !== "");
      if (hasFilters) {
        setSource(location.state.source || "filteredSource");
      }
      setSelectedFilter("");
    } else {
      setInput(null);
      setSelectedFilter("");
      setSource("noSource");
    }
  }, [location.state]);

  useEffect(() => setPage(0), [input]);

  const handleFilterChange = (event) => {
    const selectedFilterName = event.target.value;
    if (selectedFilterName === "manage") {
      setIsManageFiltersOpen(true);
      return;
    }
    setSelectedFilter(selectedFilterName);
    if (selectedFilterName) {
      const filter = savedFilters.find((f) => f.filterName === selectedFilterName);
      if (filter) {
        const filterCriteria = JSON.parse(filter.savedFilterJson);
        navigate("/inventory", { state: { criteria: filterCriteria, filterName: selectedFilterName } });
      }
    } else {
      navigate("/inventory", { state: null });
    }
  };

  const handleClearFilters = () => {
    setSource("noSource");
    setSelectedFilter("");
    setInput(null);
    navigate("/inventory", { state: null });
    resetBondCharacteristicId();
  };

  const handleDownloadCSV = (format = "xlsx") => {
    setCsvLoading(true);
    downloadCSV(token, input, totalElements, selectedColumns, format, "inventory", {
      sortConfig: sortConfigForFetchingData,
      sectionKey: "inventory",
    })
      .then(() => setCsvLoading(false))
      .catch(() => setCsvLoading(false));
  };

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (index, rowData) => {
    setSelectCusip(rowData.cusip);
    setInventoryId(rowData.bondInventoryId);
    // Ladder-linked characteristics stamp the order with their rung and route to the
    // trading desk instead of the inventory row's dealer
    const isLadderLinked = Boolean(characteristicId && bondLadderId);
    const updatedOrder = {
      cusip: rowData.cusip,
      // Upper-cased to match the Side dropdown's BUY / SELL options: for a characteristic's
      // bond the field is locked to the row's side, and a value outside the options would
      // render blank with no way to correct it
      side: String(rowData.action || rowData.side || "").trim().toUpperCase(),
      price: rowData.price,
      qty: rowData.qty,
      characteristicId: characteristicId,
      bondLadderId: isLadderLinked ? bondLadderId : null,
      bondLadderRungId: isLadderLinked ? bondLadderRungId ?? null : null,
      // Both branches state the staging flag explicitly rather than letting the non-ladder
      // case inherit it. The store setter MERGES and this handler does not reset the draft
      // first, so a bare { venue } would carry over whatever flag the previous draft held.
      // Closing the modal does reset the store (OrderEntry.handleClose), but that is the
      // only thing clearing it and the unmount safety net there is commented out - so being
      // explicit here is what keeps a live order out of the Staging Blotter.
      ...(isLadderLinked
        ? stagingOrderFields(rowData.brokerDealer)
        : liveOrderFields(rowData.brokerDealer)),
      protocol: "CLOB"  // Set protocol to CLOB
    };
    setActualOrderDataZus(updatedOrder);
    setOeOpen(true);
  };

  const handleOEClick = (index, rowData) => {
    setOeOpen(true);
  };    

  const handleCusipClick = async (index, rowData) => {
    setBondDetailsLoading(true);
    setBondDetailsOpen(true);
    try {
      const details = await fetchSecurityInfo(token, rowData.cusip || rowData.security);
      setSelectedBondDetails(details);
    } catch (e) {
      setSelectedBondDetails({ error: true, message: e.message || "Failed to fetch bond details." });
    } finally {
      setBondDetailsLoading(false);
    }
  };

  const handleDeleteChip = (keyToDelete) => {
    setInput((prevInput) => {
      if (!prevInput) return null;
      
      const updatedInput = { ...prevInput };
      if (keyToDelete.startsWith("min") || keyToDelete.startsWith("max")) {
        const baseKey = keyToDelete.replace(/^(min|max)/, "");
        delete updatedInput[`min${baseKey}`];
        delete updatedInput[`max${baseKey}`];
      } else {
        delete updatedInput[keyToDelete];
      }
      
      const hasRemainingFilters = Object.keys(updatedInput).some(key => key !== "source");
      
      if (!hasRemainingFilters) {
        navigate("/inventory", { state: {} });
        return null;
      } else {
        const stateToUpdate = { 
          ...updatedInput,
          source: "filteredSource" // Explicitly set source in navigation state
        };
        
        navigate("/inventory", { state: stateToUpdate });
        return updatedInput;
      }
    });
    
    setSelectedFilter("");
    resetBondCharacteristicId();
  };

  const handleDeleteFilter = async (filterName) => {
    try {
      await deleteFilter(token, savedFilters.find((f) => f.filterName === filterName).savedFiltersId, filterName);
      alert("Filter deleted successfully");
      setSavedFilters((prevFilters) => prevFilters.filter((filter) => filter.filterName !== filterName));
      
      // Clear the filter if it's currently applied
      if (selectedFilter === filterName) {
        setSelectedFilter("");
        setInput(null);
        navigate("/inventory", { state: {} });
      }
    } catch (error) {
      alert("Error deleting filter");
      console.error("Error deleting filter:", error);
    }
  };

  const handleEditFilter = (filter) => {
    console.log("Edit filter:", filter);
    
    // Create a copy to avoid modifying the original filter
    if (filter) {
      const filterData = {...filter};
      
      // If coming from yield table, keep pure dates and source; otherwise convert to years
      if (filterData.source !== 'yieldtable') {
        if (filterData.minMaturity) {
          filterData.minMaturity = convertMaturityToYears(filterData.minMaturity);
        }
        if (filterData.maxMaturity) {
          filterData.maxMaturity = convertMaturityToYears(filterData.maxMaturity);
        }
      }
      
      // Navigate to filter popup with filter data including savedFiltersId
      navigate("/search-inventory", { 
        state: {  
          isEdit: true, 
          filterData: filterData
        } 
      });
    } else {
      navigate("/search-inventory", { 
        state: {  
          isEdit: true, 
          filterData: filter
        } 
      });
    }
  };

// New handler specifically for saved filter editing (same as manage filters modal)
const handleEditSavedFilter = (filter) => {
  // Navigate to filter popup with filter data including savedFiltersId
  navigate("/search-inventory", { 
    state: {  
      isEdit: true, 
      filterName: filter.filterName,
      savedFiltersId: filter.savedFiltersId, 
      filterData: JSON.parse(filter.savedFilterJson)
    } 
  });
};

  return (
    <>
      <InventoryHeader
        handleOEClick={handleOEClick}
        input={input}
        selectedFilter={selectedFilter}
        savedFilters={savedFilters}
        totalElements={totalElements}
        loading={loading}
        lastUpdatedAt={lastUpdatedAt}
        csvLoading={csvLoading}
        customizeButtonRef={customizeButtonRef}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onDeleteChip={handleDeleteChip}
        onManageFiltersOpen={() => setIsManageFiltersOpen(true)}
        onAddFilter={handleEditFilter}
        onEditSavedFilter={handleEditSavedFilter} // New prop
        onCustomizeClick={() => setCcOpen(true)}
        onDownloadCSV={handleDownloadCSV}
         onEditFilter={handleEditFilter}
       />
      <InventoryTable
        bondData={bondData}
        selectedColumns={selectedColumns}
        page={page}
        itemsPerPage={itemsPerPage}
        totalElements={totalElements}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={handleRowClick}
        onCusipClick={handleCusipClick}
        onSort={handleSortingDataFetching}
        sortConfig={sortConfig} // Add this prop
      />
      <OrderEntry
        open={oeOpen}
        onClose={() => setOeOpen(false)}
        source="inventory"
        initialData={{ ...sampleData, cusip: selectCusip, inventoryId: inventoryId, bondInventoryId: inventoryId }}
        token={token}
        readOnly={readOnly}
      />
      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={setSelectedColumns}
        anchorEl={customizeButtonRef.current}
        sectionKey="inventory"
      />
      <ManageFiltersModal
        open={isManageFiltersOpen}
        onClose={() => setIsManageFiltersOpen(false)}
        filters={savedFilters}
        onDeleteFilter={handleDeleteFilter}
      />
      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
        loading={bondDetailsLoading}
      />
      
      {/* New modal for updating characteristic status */}
      <UpdateCharacteristicsStatusModal
        open={updateStatusModalOpen}
        onClose={() =>{
           setUpdateStatusModalOpen(false);
          //  resetBondCharacteristicId();
        }}
        onUpdateStatus={handleUpdateCharacteristicStatus}
      />
    </>
  );
};

export default Inventory;
